<template>
  <Card>
    <div class="metric">
      <div
        v-if="icon"
        class="metric__icon"
      >
        <Icon :type="icon" />
      </div>

      <div class="flex flex-col">
        <!-- Header -->
        <div class="metric__header">
          <div class="metric__info">
            <div class="metric__title">{{ title }}</div>
            <div
              v-if="description"
              class="metric__description"
            >
              {{ description }}
            </div>
          </div>
        </div>

        <div
          class="flex"
          :class="{ 'flex-row items-center gap-2': compact, 'flex-col': !compact }"
        >
          <!-- Main animated + formatted value -->
          <div class="metric__value">
            {{ displayValue }}
          </div>

          <!-- Change indicator -->
          <div
            v-if="change !== null && !compact"
            class="metric__change"
          >
            <span
              class="font-bold mr-0.5"
              :class="{
                'text-success': change > 0,
                'text-error': change < 0,
                'text-muted': change === 0
              }"
            >
              <Icon :type="change > 0 ? 'chevron-up' : 'chevron-down'" />
              {{ change }}%
            </span>
            <span
              v-if="changeText"
              class="text-quaternary"
            >
              {{ changeText }}
            </span>
          </div>

          <div
            v-if="change !== null && compact"
            class="metric__change"
          >
            <Chip
              v-tippy="{ content: changeText }"
              :color="change > 0 ? 'success' : 'error'"
              size="xs"
              :rounded="false"
              :icon="change > 0 ? 'chevron-up' : 'chevron-down'"
              :severity="change > 0 ? 'success' : 'error'"
            >
              {{ change }}%
            </Chip>
          </div>
        </div>
      </div>

      <!-- Optional graph slot -->
      <div
        v-if="hasGraph"
        class="metric__graph"
      >
        <slot name="graph"></slot>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import Card from '@/components/card/Card.vue'
import Icon from '../icon/Icon.vue'
import Chip from '../chip/Chip.vue'

const props = defineProps({
  icon: { type: String, default: '' },
  change: { type: Number, default: null },
  changeText: { type: String, default: '' },
  hasGraph: { type: Boolean, default: false },
  modelValue: { type: Number, default: 0 },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  compact: { type: Boolean, default: false }
})

defineEmits(['update:modelValue'])

// Local state that we animate
const localValue = ref<number>(0)

// When component mounts, animate from 0 -> modelValue
onMounted(() => {
  animateValue(0, props.modelValue, 800) // 800ms duration (tweak as desired)
})

// Watch for changes to modelValue and animate
watch(
  () => props.modelValue,
  (newVal, oldVal) => {
    // If oldVal not defined (first pass), start from 0
    animateValue(oldVal ?? 0, newVal, 800)
  }
)

// Format localValue with commas
const displayValue = computed(() => {
  return new Intl.NumberFormat('en-GB').format(localValue.value)
})

/**
 * Animate the counter from start -> end over 'duration' ms
 * using requestAnimationFrame
 */
function animateValue(start: number, end: number, duration: number) {
  if (start === end) {
    localValue.value = end
    return
  }
  let startTime: number = 0

  function step(timestamp: number) {
    if (!startTime) startTime = timestamp
    const elapsed: number = timestamp - startTime
    const progress: number = Math.min(elapsed / duration, 1)
    localValue.value = Math.floor(start + (end - start) * progress)

    if (progress < 1) {
      window.requestAnimationFrame(step)
    }
  }

  window.requestAnimationFrame(step)
}
</script>

<style scoped>
.metric {
  display: flex;
  flex-direction: row;
  padding: var(--s-4);
  gap: var(--s-2);
}

.metric__header {
  display: flex;
  align-items: center;
}

.metric__icon {
  width: 32px;
  height: 32px;
  margin-right: 0.75rem;
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric__icon img {
  width: 100%;
  height: auto;
  display: block;
}

.metric__info {
  display: flex;
  flex-direction: column;
}

.metric__title {
  color: var(--text-tertiary);
  font-weight: var(--font-semibold);
  font-size: var(--text-sm);
}

.metric__description {
  font-size: var(--text-sm);
  color: var(--text-quaternary);
}

.metric__value {
  font-size: var(--text-2xl);
  font-weight: 700;
}

.metric__change {
  display: flex;
  align-items: baseline;
  font-size: 0.875rem;
}

/* Compact layout */
.metric.compact {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.metric.compact .metric__header {
  margin-bottom: 0;
}

.metric.compact .metric__value {
  margin: 0;
}

.metric.compact .metric__graph {
  margin: 0;
}
</style>
